import { createClient, SupabaseClient } from '@supabase/supabase-js';

// These environment variables will be provided by the user in Vercel
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Helper to check if Supabase is configured
export const isSupabaseConfigured = () => {
  return supabaseUrl !== '' && supabaseAnonKey !== '';
};

// Fetch wrapper with timeout to prevent hung requests
// 30s is generous enough for saves but still prevents infinite hangs
const fetchWithTimeout = (url: RequestInfo | URL, options: RequestInit = {}) => {
  const controller = new AbortController();
  const urlString = typeof url === 'string' ? url : url instanceof URL ? url.toString() : String(url);
  const isEdgeFunctionCall = urlString.includes('/functions/v1/');
  const timeoutMs = isEdgeFunctionCall ? 180000 : 30000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  if (options.signal) {
    options.signal.addEventListener('abort', () => controller.abort());
  }

  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timeoutId));
};

// Only create the client if credentials are provided
// This prevents the "supabase URL is required" error
export const supabase: SupabaseClient | null = isSupabaseConfigured()
  ? createClient(supabaseUrl, supabaseAnonKey, {
      global: { fetch: fetchWithTimeout },
      auth: {
        // Bypass Web Locks API which can get stuck and block ALL operations.
        // The default lock implementation uses navigator.locks.request() which
        // can deadlock when a previous auth operation didn't release its lock.
        // This simple passthrough avoids the deadlock entirely.
        lock: async (_name: string, _acquireTimeout: number, fn: () => Promise<any>) => {
          return fn();
        },
      },
    })
  : null;
